import { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router";
import { useIsYearInReviewOwner, SummonerInfo } from "@ugg/shared/hooks/use-is-year-in-review-owner";
import { YearInReviewContent } from "./components/YearInReviewContent";
import { YearInReviewGeneratePage } from "./components/YearInReviewGeneratePage";
import { YearInReviewGeneratingPage } from "./components/YearInReviewGeneratingPage";
import { YearInReviewUnavailable } from "./components/YearInReviewUnavailable";
import { YearInReviewFailed } from "./components/YearInReviewFailed";
import { YearInReviewNoMatches } from "./components/YearInReviewNoMatches";
import {
  getUserYearInReviewStatus,
  YIRState,
  useInsertUserIntoYirQueue,
} from "@ugg/shared/api/requests/summoner-profiles/year-in-review";
import { useSummonerProfileInitSimple } from "@ugg/shared/api/requests/summoner-profiles/profile-init";

interface YearInReviewContainerProps {
  onContactSupport?: () => void;
  summonerInfo?: SummonerInfo;
}

export function YearInReviewContainer({ onContactSupport, summonerInfo }: YearInReviewContainerProps) {
  const { region, riotUserName, riotTagLine } = useParams<{
    region: string;
    riotUserName: string;
    riotTagLine: string;
  }>();

  const [status, setStatus] = useState<YIRState>(YIRState.NOT_STARTED);
  const [hasError, setHasError] = useState(false);

  const {
    data: profileData,
    loading: isloadingProfile,
    error,
  } = useSummonerProfileInitSimple(region, riotUserName, riotTagLine, {});

  const { profileInitSimple } = profileData || {};
  const { playerInfo } = profileInitSimple || {};
  const { iconId } = playerInfo || {};
  const {
    data: yirStatus,
    loading: loadingYIR,
    error: errorYIR,
    refetch,
  } = getUserYearInReviewStatus(riotUserName, riotTagLine, region, "25");

  useEffect(() => {
    if (!loadingYIR && yirStatus) {
      setStatus(yirStatus);
      setHasError(false);
    }
    if (errorYIR) {
      setHasError(true);
    }
  }, [loadingYIR, yirStatus, errorYIR]);

  const { isOwner } = useIsYearInReviewOwner(region, riotUserName, riotTagLine, summonerInfo);
  const [isGenerating, setIsGenerating] = useState(false);
  const { insertIntoQueue } = useInsertUserIntoYirQueue(25);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setHasError(false);
    try {
      await insertIntoQueue();
      const result = await refetch();
      if (result.data?.yearInReviewStatusRequest) {
        setStatus(result.data.yearInReviewStatusRequest);
      }
    } catch (e) {
      setHasError(true);
      setStatus(YIRState.ERROR);
      console.error("Generate or Refetch error:", e);
    } finally {
      setIsGenerating(false);
    }
  }, [insertIntoQueue, refetch]);

  const isPolling = YIRState.IN_QUEUE || YIRState.PROCESSING;
  const isLoadingScreen = isloadingProfile || (loadingYIR && !isPolling);

  if (isLoadingScreen) {
    return (
      <div className="w-full flex justify-center h-[600px] p-[18px] max-md:p-[12px] rounded-[3px]">
        <div className="spinthatshit-loader">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (hasError || status === YIRState.ERROR) {
      return <YearInReviewFailed onRetry={handleGenerate} onContactSupport={onContactSupport} />;
    }

    if (status === YIRState.GENERATED) {
      return <YearInReviewContent region={region} riotUserName={riotUserName} riotTagLine={riotTagLine} iconId={iconId} />;
    }

    if (status === YIRState.NO_MATCHES) {
      return <YearInReviewNoMatches onRetry={handleGenerate} onContactSupport={onContactSupport} />;
    }

    if (isOwner) {
      if (status === YIRState.NOT_STARTED) {
        return (
          <YearInReviewGeneratePage
            onGenerate={handleGenerate}
            riotUserName={riotUserName}
            riotTagLine={riotTagLine}
            isLoading={isGenerating}
          />
        );
      } else if (status === YIRState.IN_QUEUE || status === YIRState.PROCESSING) {
        return <YearInReviewGeneratingPage />;
      }
    }
    return <YearInReviewUnavailable />;
  };

  return <YearInReviewLayout>{renderContent()}</YearInReviewLayout>;
}

export function YearInReviewLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full h-[100vh] flex-1 flex flex-col">
      <div
        className="absolute top-0 w-full aspect-[1918/1132] shrink-0 pointer-events-none z-0 -mt-[100px]"
        style={{
          background: `
            linear-gradient(180deg, rgba(7, 7, 32, 0.00) 0%, #070720 100%),
            radial-gradient(100% 100% at 50% 0%, rgba(7, 7, 32, 0.56) 0%, #070720 100%),
            url('https://static.bigbrain.gg/assets/ugg/cta/year-in-review-karma-full.jpg') lightgray 50% / cover no-repeat
          `,
        }}
      />
      <div className="relative z-10 w-full mx-auto px-4 mt-[100px] max-xs:mt-0">{children}</div>
    </div>
  );
}
